import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { HighYieldExplanation, PromptLabPage } from './app/(dashboard)/ai/index';
import { ReIndex } from './app/(dashboard)/algolia';
import { ManageAws, ManageImage, ManageVideo } from './app/(dashboard)/asset';
import {
  BatchImport,
  BatchOverwrite,
  BatchPublic,
  BlogListPage,
  ChapterEditPage,
  Clone,
  CommentsPage,
  DeleteRecord,
  JobCreate,
  JobDetail,
  JobExample,
  JobList,
  QueriesReviewPage,
  QuestionEditPage,
  QuestionReviewPage,
  Templates,
  UcatQuestions,
  ValidateBook,
  ValidateMockTest,
  ValidateQuestion,
  ValidateStation,
  WeeklySqlReviewPage,
  WeeklySqlViewPage,
} from './app/(dashboard)/content';
import { StationEditPage } from './app/(dashboard)/content/edit/station/[id]';
import { CacheManager, CronManager, DbNotification, RefreshMtv } from './app/(dashboard)/database';
import RootLayout from './app/(dashboard)/layout';
import {
  OsceCalculatorPage,
  OsceSessionDetailPage,
  OsceSessionsPage,
  OsceStationsPage,
  OsceStatsPage,
} from './app/(dashboard)/osce';
import { OsceStatsDetailRedirect } from './app/(dashboard)/osce/osce-stats-detail-redirect';
import Dashboard from './app/(dashboard)/page';
import { SyncContact } from './app/(dashboard)/sendgrid';
import { SettingsPromptsPage } from './app/(dashboard)/settings';
import { SsoConnectionDetailPage, SsoDashboardPage } from './app/(dashboard)/sso';
import { StudyGroupDetailPage, StudyGroupSettingsPage, StudyGroupsPage } from './app/(dashboard)/study-group';
import { Transactions, UserFeedbacks } from './app/(dashboard)/subscription';
import Temp from './app/(dashboard)/temp/page';
import { TutorSessionDetailPage, TutorSessionsPage, TutorStatsPage } from './app/(dashboard)/tutor';
import { UniversityAccountDetailPage, UniversityAccountsPage } from './app/(dashboard)/university-accounts';
import { UserInfo, UserSessions, UserSubs } from './app/(dashboard)/user';
import Login from './app/auth/login/page';
import Loading from './app/loading';
import NotFound from './app/not-found';
import { ROUTES } from './config';
import { PrivateRoutes } from './router';
import { VersionManager } from './router/version-manager';
import { useAuthState } from './store/auth';

function App() {
  const globalLoading = false;
  const { isAuthenticated } = useAuthState();

  if (globalLoading) {
    return <Loading />;
  }

  return (
    <>
      <VersionManager />
      <Routes>
        <Route element={<PrivateRoutes />}>
          <Route element={<RootLayout />}>
            <Route path={ROUTES.MAIN} Component={Dashboard} />
            <Route path={ROUTES.CONTENT.VALIDATE.QUESTION} Component={ValidateQuestion} />
            <Route path={ROUTES.CONTENT.VALIDATE.STATION} Component={ValidateStation} />
            <Route path={ROUTES.CONTENT.VALIDATE.MOCK_TEST} Component={ValidateMockTest} />
            <Route path={ROUTES.CONTENT.VALIDATE.BOOK} Component={ValidateBook} />
            <Route path={ROUTES.CONTENT.VALIDATE.UCAT} Component={UcatQuestions} />
            <Route path={ROUTES.CONTENT.DELETE.MAIN} Component={DeleteRecord} />
            <Route path={ROUTES.CONTENT.TEMPLATE} Component={Templates} />
            <Route path={ROUTES.CONTENT.CLONE} Component={Clone} />
            <Route
              path={ROUTES.AI.HIGH_YIELD}
              element={
                <Suspense fallback={<Loading />}>
                  <HighYieldExplanation />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.AI.PROMPT_LAB}
              element={
                <Suspense fallback={<Loading />}>
                  <PromptLabPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.OSCE.STATS}
              element={
                <Suspense fallback={<Loading />}>
                  <OsceStatsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.OSCE.SESSIONS}
              element={
                <Suspense fallback={<Loading />}>
                  <OsceSessionsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.OSCE.STATIONS}
              element={
                <Suspense fallback={<Loading />}>
                  <OsceStationsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.OSCE.CALCULATOR}
              element={
                <Suspense fallback={<Loading />}>
                  <OsceCalculatorPage />
                </Suspense>
              }
            />
            <Route path={ROUTES.OSCE.STATS_DETAIL_LEGACY} element={<OsceStatsDetailRedirect />} />
            <Route
              path={ROUTES.OSCE.SESSION_DETAIL}
              element={
                <Suspense fallback={<Loading />}>
                  <OsceSessionDetailPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.TUTOR.STATS}
              element={
                <Suspense fallback={<Loading />}>
                  <TutorStatsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.TUTOR.SESSIONS}
              element={
                <Suspense fallback={<Loading />}>
                  <TutorSessionsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.TUTOR.SESSION_DETAIL}
              element={
                <Suspense fallback={<Loading />}>
                  <TutorSessionDetailPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.STUDY_GROUP.GROUPS}
              element={
                <Suspense fallback={<Loading />}>
                  <StudyGroupsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.STUDY_GROUP.GROUP_DETAIL}
              element={
                <Suspense fallback={<Loading />}>
                  <StudyGroupDetailPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.STUDY_GROUP.SETTINGS}
              element={
                <Suspense fallback={<Loading />}>
                  <StudyGroupSettingsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.SSO.DASHBOARD}
              element={
                <Suspense fallback={<Loading />}>
                  <SsoDashboardPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.SSO.DETAIL}
              element={
                <Suspense fallback={<Loading />}>
                  <SsoConnectionDetailPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.UNIVERSITY_ACCOUNTS.LIST}
              element={
                <Suspense fallback={<Loading />}>
                  <UniversityAccountsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.UNIVERSITY_ACCOUNTS.DETAIL}
              element={
                <Suspense fallback={<Loading />}>
                  <UniversityAccountDetailPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.SETTINGS.PROMPTS}
              element={
                <Suspense fallback={<Loading />}>
                  <SettingsPromptsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.EDIT.QUESTION}
              element={
                <Suspense fallback={<Loading />}>
                  <QuestionEditPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.EDIT.CHAPTER}
              element={
                <Suspense fallback={<Loading />}>
                  <ChapterEditPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.EDIT.STATION}
              element={
                <Suspense fallback={<Loading />}>
                  <StationEditPage />
                </Suspense>
              }
            />
            <Route path={ROUTES.CONTENT.BATCH.IMPORT} Component={BatchImport} />
            <Route path={ROUTES.CONTENT.BATCH.OVERWRITE} Component={BatchOverwrite} />
            <Route path={ROUTES.CONTENT.BATCH.PUBLIC} Component={BatchPublic} />

            <Route
              path={ROUTES.CONTENT.BLOG.MAIN}
              element={
                <Suspense fallback={<Loading />}>
                  <BlogListPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.JOB.LIST}
              element={
                <Suspense fallback={<Loading />}>
                  <JobList />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.JOB.DETAIL}
              element={
                <Suspense fallback={<Loading />}>
                  <JobDetail />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.JOB.CREATE}
              element={
                <Suspense fallback={<Loading />}>
                  <JobCreate />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.JOB.EXAMPLE}
              element={
                <Suspense fallback={<Loading />}>
                  <JobExample />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.REVIEW.COMMENTS}
              element={
                <Suspense fallback={<Loading />}>
                  <CommentsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.REVIEW.QUESTIONS}
              element={
                <Suspense fallback={<Loading />}>
                  <QuestionReviewPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.REVIEW.WEEKLY_SEARCH}
              element={
                <Suspense fallback={<Loading />}>
                  <WeeklySqlReviewPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.REVIEW.WEEKLY_SQL_VIEW}
              element={
                <Suspense fallback={<Loading />}>
                  <WeeklySqlViewPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.CONTENT.REVIEW.QUERIES}
              element={
                <Suspense fallback={<Loading />}>
                  <QueriesReviewPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.ASSET.IMAGE.MAIN}
              element={
                <Suspense fallback={<Loading />}>
                  <ManageImage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.ASSET.AWS.MAIN}
              element={
                <Suspense fallback={<Loading />}>
                  <ManageAws />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.ASSET.VIDEO.MAIN}
              element={
                <Suspense fallback={<Loading />}>
                  <ManageVideo />
                </Suspense>
              }
            />
            <Route path={ROUTES.ALGOLIA.MAIN} Component={ReIndex} />
            <Route path={ROUTES.SENDGRID.MAIN} Component={SyncContact} />
            <Route
              path={ROUTES.USER.SESSIONS}
              element={
                <Suspense fallback={<Loading />}>
                  <UserSessions />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.USER.INFO}
              element={
                <Suspense fallback={<Loading />}>
                  <UserInfo />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.USER.SUBS}
              element={
                <Suspense fallback={<Loading />}>
                  <UserSubs />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DATABASE.NOTIFICATION}
              element={
                <Suspense fallback={<Loading />}>
                  <DbNotification />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DATABASE.REFRESH_MTV}
              element={
                <Suspense fallback={<Loading />}>
                  <RefreshMtv />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DATABASE.CRON}
              element={
                <Suspense fallback={<Loading />}>
                  <CronManager />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.DATABASE.CACHE}
              element={
                <Suspense fallback={<Loading />}>
                  <CacheManager />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.SUBSCRIPTION.TRANSACTIONS}
              element={
                <Suspense fallback={<Loading />}>
                  <Transactions />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.SUBSCRIPTION.REVIEWS}
              element={
                <Suspense fallback={<Loading />}>
                  <UserFeedbacks />
                </Suspense>
              }
            />
            <Route path={ROUTES.TEMP} Component={Temp} />
          </Route>
        </Route>

        <Route
          path={ROUTES.LOGIN}
          element={isAuthenticated ? <Navigate to={ROUTES.MAIN} replace /> : <Login />}
        />
        {/* <Route
          path={ROUTES.REGISTER}
          element={isAuthenticated ? <Navigate to={ROUTES.MAIN} replace /> : <Register />}
        /> */}
        <Route path='*' Component={NotFound} />
      </Routes>
    </>
  );
}

export default App;
